import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useSite } from './SiteContext';

export interface AIMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  actions?: ActionButton[];
  metadata?: Record<string, any>;
}

export interface ActionButton {
  label: string;
  action: 'navigate' | 'api_call' | 'dismiss';
  path?: string;
  handler?: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
}

export interface RouteContext {
  path: string;
  siteId: string | null;
  siteName: string | null;
}

interface AIContextState {
  isOpen: boolean;
  messages: AIMessage[];
  context: RouteContext;
  activeWizardId: string | null;
  isLoading: boolean;
}

interface AIContextValue extends AIContextState {
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  addMessage: (message: AIMessage) => void;
  clearHistory: () => void;
  setActiveWizardId: (id: string | null) => void;
  setIsLoading: (loading: boolean) => void;
}

const AIContext = createContext<AIContextValue | undefined>(undefined);

const STORAGE_KEY = 'global-ai-chat-session';
const SESSION_EXPIRY_HOURS = 24;

interface StoredSession {
  messages: Array<{
    role: string;
    content: string;
    timestamp: string;
  }>;
  expiresAt: string;
}

export function AIProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { selectedSite } = useSite();

  // Initialize from localStorage if valid
  const [state, setState] = useState<AIContextState>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const session: StoredSession = JSON.parse(stored);
        const expiresAt = new Date(session.expiresAt);

        // Check if session is still valid
        if (expiresAt > new Date()) {
          return {
            isOpen: false,
            messages: session.messages.map(msg => ({
              ...msg,
              timestamp: new Date(msg.timestamp),
            })) as AIMessage[],
            context: {
              path: location.pathname,
              siteId: selectedSite?.id || null,
              siteName: selectedSite?.name || null,
            },
            activeWizardId: null,
            isLoading: false,
          };
        }
      }
    } catch (error) {
      console.error('Failed to restore AI chat session:', error);
    }

    // Default state with welcome message
    return {
      isOpen: false,
      messages: [
        {
          role: 'assistant',
          content: "Hi! I'm your AI assistant. I can help you create pages, answer questions, and guide you through the dashboard. What would you like to do?",
          timestamp: new Date(),
        },
      ],
      context: {
        path: location.pathname,
        siteId: selectedSite?.id || null,
        siteName: selectedSite?.name || null,
      },
      activeWizardId: null,
      isLoading: false,
    };
  });

  // Update context when route or site changes
  useEffect(() => {
    setState(prev => ({
      ...prev,
      context: {
        path: location.pathname,
        siteId: selectedSite?.id || null,
        siteName: selectedSite?.name || null,
      },
    }));
  }, [location.pathname, selectedSite]);

  // Persist to localStorage when messages change
  useEffect(() => {
    if (state.messages.length > 0) {
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + SESSION_EXPIRY_HOURS);

      const session: StoredSession = {
        messages: state.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp.toISOString(),
        })),
        expiresAt: expiresAt.toISOString(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    }
  }, [state.messages]);

  const openChat = () => setState(prev => ({ ...prev, isOpen: true }));
  const closeChat = () => setState(prev => ({ ...prev, isOpen: false }));
  const toggleChat = () => setState(prev => ({ ...prev, isOpen: !prev.isOpen }));

  const addMessage = (message: AIMessage) => {
    setState(prev => ({
      ...prev,
      messages: [...prev.messages, message],
    }));
  };

  const clearHistory = () => {
    localStorage.removeItem(STORAGE_KEY);
    setState(prev => ({
      ...prev,
      messages: [
        {
          role: 'assistant',
          content: "Chat cleared! How can I help you?",
          timestamp: new Date(),
        },
      ],
      activeWizardId: null,
    }));
  };

  const setActiveWizardId = (id: string | null) => {
    setState(prev => ({ ...prev, activeWizardId: id }));
  };

  const setIsLoading = (loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }));
  };

  const value: AIContextValue = {
    ...state,
    openChat,
    closeChat,
    toggleChat,
    addMessage,
    clearHistory,
    setActiveWizardId,
    setIsLoading,
  };

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
}

export function useAI() {
  const context = useContext(AIContext);
  if (context === undefined) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
}
